// REACT AND OTHER DEPS
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query"

// ICONS AND ASSETS
import contestIcon from "../assets/icons/ChallengeIcon.png";
import { FaCircleCheck, FaRegCircle } from "react-icons/fa6";
import { IoIosArrowBack } from "react-icons/io";

// STYLES
import "../styles/Challenge.scss";

// CUSTOM
import CLink from "../components/CLink";
import axios from "../scripts/axiosConfig";
import { useAuth } from "../contexts/AuthContext";
import { useMisc } from "../contexts/MiscContext";
import Tag from "../components/Tag";

type challengeDataType = {
    data: {
        success: boolean,
        message: string,
        data: {
            challenge: {
                _id: string,
                name: string,
                short_description: string,
                description: string,
                tags: string[],
                problems: {
                    [key: string]: {
                        title: string,
                        difficulty: string,
                        status: string
                    }
                },
                challengeURL: string,
                __v: number
            },
            problems: {
                [key: string]: {
                    title: string,
                    difficulty: string,
                    status: string
                }
            }
        },
        error: any
    } | undefined,
    error: any,
    isError: boolean,
    isLoading: boolean,
    isSuccess: boolean,
}

export default function Challenges() {

    // HOOKS
    const { currentUser } = useAuth();
    const { challengeNameToPageTitle, convertSnakeTagsToTitle } = useMisc();
    const { challengeID } = useParams();
    const location = useLocation();

    // STATE
    const [pageTitle, setPageTitle] = useState(challengeID);
    const [challengeName, setChallengeName] = useState("");

    const skeletonProblems = {
        SKL1: {
            title: "Problem 1",
            difficulty: "easy",
            status: ""
        },
        SKL2: {
            title: "Problem 2",
            difficulty: "medium",
            status: ""
        },
        SKL3: {
            title: "Problem 3",
            difficulty: "hard",
            status: ""
        }
    }

    const difficultyColors = {
        "easy": "colorGreen",
        "medium": "colorYellow",
        "hard": "colorRed"
    };

    const { isLoading, error, data, isSuccess, isError }: challengeDataType = useQuery({
        queryKey: [`challenge${challengeID}Data`],
        queryFn: async () => {
            const res = await axios.get(
                `/challenges/get/${challengeID}`, 
            );
            console.log(res.data);
            return res.data;
          },
        staleTime: 0,
        refetchOnWindowFocus: false
    });

    useEffect(() => {
        if (data) {
            setChallengeName( challengeNameToPageTitle(data?.data?.challenge?.name) );
            setPageTitle(`${challengeNameToPageTitle(data?.data?.challenge?.name)} - Challenge`);
        }
    }, [data]);

    useEffect(() => {
        if (isError) {
            toast.error(error);
        }
    }, [isError]);

    useEffect(() => {
        console.log("From: ")
        console.log(location.state);
    }, [])


    return (
        <>
            <Helmet>
                <title>{ (pageTitle) ? `${pageTitle} - Codify` : 'Challenge - Codify' }</title>
            </Helmet>
        
            <div className="challenges">
                
                <div className="challengeCoverImage">
                    {
                        (!!(location.state?.from) && !(location?.pathname === location?.state?.from) ) && (
                            <CLink to={location?.state?.from} className="backButton"> <IoIosArrowBack /> </CLink>
                        )
                    }
                    <div className="info">
                        <div className="icon">
                            <img src={contestIcon}/>
                        </div>
                        <div className="text" onDoubleClick={() => console.log(location)} >
                            <div className={("title " + (isLoading && "loading") )}>{data?.data?.challenge?.name || null}</div>
                            <div className={("about " + (isLoading && "loading") )}>{data?.data?.challenge?.description || null}</div>

                            {
                                (isLoading) && (
                                    <div className="tags loading">
                                        <div className="tag loading">please</div>
                                        <div className="tag loading">wait</div>
                                        <div className="tag loading">loading</div>
                                    </div>
                                )
                            }
                            {
                                (isSuccess) && (
                                    <div className="tags">
                                        {
                                            data?.data?.challenge?.tags.map((tag, index) => {
                                                return (
                                                    <Tag 
                                                        tag={tag}
                                                        key={index}
                                                    />
                                                )
                                            })
                                        }
                                    </div>
                                )
                            }
                            
                        </div>
                    </div>
                </div>

                <div className="problemsContainer">
                    <div className="problemsList">
                        
                        {
                            ( isSuccess && data?.data?.problems ) && (
                                Object.keys(data?.data?.problems).map((problemID, index) => {
                                    return (
                                    <Link 
                                        to={`/code/${problemID}?challenge=${challengeID}`}
                                        state={{
                                            from: location.pathname, 
                                            challengeID: challengeID,
                                            challengeName: challengeName
                                        }} 
                                        target="_blank" 
                                        className={`problem ${problemID}`} 
                                        key={index}
                                    >
                                        <div className="left">
                                            <div className={`completed ${data?.data?.problems[problemID].status === "completed" ? "completed" : ""}`}>
                                                {data?.data?.problems[problemID].status === "completed" ? <FaCircleCheck /> : <FaRegCircle />}
                                            </div>
                                            <div className={`title ${data?.data?.problems[problemID].title ? "" : "loading"}`}>{data?.data?.problems[problemID].title}</div>
                                        </div>
                                        <div 
                                            className={`difficulty ${difficultyColors[data?.data?.problems[problemID].difficulty as keyof typeof difficultyColors]}`}
                                        >
                                            {data?.data?.problems[problemID].difficulty}
                                        </div>
                                    </Link>
                                    )
                                })
                            )
                        }
                        {
                            ( isSuccess && data?.data?.problems && Object.keys(data?.data?.problems).length <= 0 ) && (
                                <div className="problem">
                                    <div className="text">No problems added in this challenge, yet!</div>
                                </div>
                            )
                        }
                        { 
                            (isLoading) && (
                                Object.keys(skeletonProblems).map((problemID, index) => {
                                    return (<div className={`problem ${problemID}`} key={index}>
                                        <div className="left">
                                            <div className="completed loading">
                                            </div>
                                            <div className={`title loading`}></div>
                                        </div>
                                        <div className={"difficulty loading"}></div>
                                    </div>)
                                })
                            )
                        } 
                    </div>
                </div>

            </div>
        </>
        
    )
}